/**
 * TransferTicketDialog - Modal M1 (Plano A — Task 10, aprovado pelo dono):
 * transferir ticket para outro operador, equipe ou pipeline.
 *
 * Base: `TransferConversationDialog` do chat (`@/shared/components/dialogs`)
 * — mesma estrutura (tabs, busca, cards selecionáveis, observações, testids
 * hardcoded light) — adaptada com uma 3ª aba (Pipeline) e chamando
 * `useTransferTicket` diretamente (self-contained, ao contrário do dialog do
 * chat que delega a mutation pro caller).
 *
 * Vive no módulo `tickets` (não em `shared/components/dialogs`) porque,
 * diferente do `TransferConversationDialog` (usado por `chat` E
 * `attendance-management` — 2 módulos distintos, o que justifica o local
 * compartilhado), este dialog só é consumido dentro de `modules/helpdesk/tickets`
 * (`TicketDetailHeader`, `TicketsPage`) — sem precedente de uso cross-módulo.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import type { TransferRequest } from '@troia-v3/types';
import { Icon } from '@/shared/icons';
import { useTeamsDropdown } from '@/modules/teams/hooks/useTeams';
import { useUsersDropdown } from '@/modules/users/hooks/useUsers';
import { usePipelinesDropdown } from '@/modules/helpdesk/pipelines/hooks/usePipelines';
import { useStagesByPipelineId } from '@/modules/helpdesk/pipelines/hooks/useStages';
import { Avatar, AvatarImage, AvatarFallback } from '@/shared/components/ui/avatar';
import { Input } from '@/shared/components/ui/Input';
import { Textarea } from '@/shared/components/ui/Textarea';
import { Select } from '@/shared/components/ui/Select';
import { useTicketAssigneesDropdown, useTransferTicket } from '../hooks/useTickets';
import { getApiErrorMessage } from '../utils';

type TransferTarget = 'user' | 'team' | 'pipeline';

export interface TransferTicketDialogProps {
  /** Modo single — 1 ticket. Ignorado se `ticketIds` vier preenchido. */
  ticketId?: string;
  ticketNumber?: string;
  ticketTitle?: string;
  /** Modo bulk (#158) — transfere N tickets pro mesmo destino (loop do endpoint). */
  ticketIds?: string[];
  isOpen: boolean;
  onClose: () => void;
  /** Chamado após um bulk bem-sucedido (ex.: limpar seleção). */
  onTransferred?: () => void;
  /**
   * Pipeline dos tickets. Com ele, a aba Usuário lista só quem participa do
   * pipeline (qualquer papel) — mesma régua que o servidor confere. Sem ele
   * (ex.: tickets de pipelines diferentes), lista todos e o servidor decide.
   */
  pipelineId?: string | null;
}

const LIGHT_THEME_OVERRIDE = {
  '--theme-text': '#1e293b',
  '--theme-muted-text': '#6b7280',
  '--theme-input-background': '#ffffff',
  '--theme-input-text': '#1e293b',
  '--theme-input-border': 'rgba(0, 0, 0, 0.10)',
  '--theme-input-focus': '#8b5cf6',
  '--theme-error-color': '#ef4444',
  '--theme-card-background': '#ffffff',
  '--theme-card-text': '#334155',
  '--theme-card-border': 'rgba(0, 0, 0, 0.06)',
  '--theme-card-muted': '#94a3b8',
  '--theme-button-primary': '#8b5cf6',
  '--theme-button-primary-text': '#ffffff',
  '--theme-button-primary-hover': '#7c3aed',
  '--theme-primary': '#8b5cf6',
  '--theme-border': 'rgba(0, 0, 0, 0.06)',
  '--theme-component-background': '#ffffff',
} as React.CSSProperties;

export const TransferTicketDialog: React.FC<TransferTicketDialogProps> = ({
  ticketId,
  ticketNumber,
  ticketTitle,
  ticketIds,
  isOpen,
  onClose,
  onTransferred,
  pipelineId,
}) => {
  // Alvos: `ticketIds` (bulk) tem prioridade; senão o `ticketId` single.
  const targetIds = useMemo(
    () => (ticketIds && ticketIds.length > 0 ? ticketIds : ticketId ? [ticketId] : []),
    [ticketIds, ticketId]
  );
  const isBulk = targetIds.length > 1;

  const [transferType, setTransferType] = useState<TransferTarget>('user');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [selectedPipelineId, setSelectedPipelineId] = useState<string | null>(null);
  const [selectedStageId, setSelectedStageId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [notes, setNotes] = useState('');

  const transferMutation = useTransferTicket();

  // Fetch data — hooks condicionados ao `isOpen` (CROSS_MODULE_PERMISSIONS):
  // hooks executam antes do `if (!isOpen) return null` abaixo, então evitamos
  // disparar as 4 queries quando o modal está fechado.
  const { data: teamsData = [] } = useTeamsDropdown({ enabled: isOpen });
  const { data: pipelineAssignees, isLoading: isLoadingPipelineAssignees } = useTicketAssigneesDropdown(pipelineId, {
    enabled: isOpen,
  });
  const { data: allActiveUsers, isLoading: isLoadingAllUsers } = useUsersDropdown({
    status: 'active',
    enabled: isOpen && !pipelineId,
  });
  const usersData = (pipelineId ? pipelineAssignees : allActiveUsers) ?? [];
  const isLoadingUsers = pipelineId ? isLoadingPipelineAssignees : isLoadingAllUsers;
  const { data: pipelinesData = [] } = usePipelinesDropdown({ enabled: isOpen });
  const { data: stagesData = [] } = useStagesByPipelineId(isOpen ? (selectedPipelineId ?? undefined) : undefined);

  // Reset ao abrir — evita vazar seleção/busca de uma transferência anterior.
  useEffect(() => {
    if (isOpen) {
      setTransferType('user');
      setSelectedUserId(null);
      setSelectedTeamId(null);
      setSelectedPipelineId(null);
      setSelectedStageId(null);
      setSearchTerm('');
      setNotes('');
    }
  }, [isOpen]);

  const filteredUsers = useMemo(
    () =>
      usersData.filter(
        (user) =>
          user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (user.email ?? '').toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [usersData, searchTerm]
  );

  const filteredTeams = useMemo(
    () =>
      teamsData.filter(
        (team) =>
          team.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          team.description?.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [teamsData, searchTerm]
  );

  const filteredPipelines = useMemo(
    () => pipelinesData.filter((pipeline) => pipeline.name.toLowerCase().includes(searchTerm.toLowerCase())),
    [pipelinesData, searchTerm]
  );

  const stageOptions = useMemo(
    () => stagesData.map((stage) => ({ value: stage.id, label: stage.name })),
    [stagesData]
  );

  const canConfirm =
    transferType === 'user'
      ? !!selectedUserId
      : transferType === 'team'
        ? !!selectedTeamId
        : !!selectedPipelineId;

  const isLoading = transferMutation.isPending;

  const handleTransfer = async () => {
    if (!canConfirm || targetIds.length === 0) return;

    const body: TransferRequest = {};
    if (transferType === 'user') {
      body.toUserId = selectedUserId;
    } else if (transferType === 'team') {
      body.toTeamId = selectedTeamId;
    } else if (selectedPipelineId) {
      body.toPipelineId = selectedPipelineId;
      if (selectedStageId) body.toStageId = selectedStageId;
    }
    const trimmedNotes = notes.trim();
    if (trimmedNotes) body.notes = trimmedNotes;

    if (!isBulk) {
      // Single — fluxo original preservado.
      try {
        const result = await transferMutation.mutateAsync({ ticketId: targetIds[0]!, body });
        toast.success('Ticket transferido com sucesso!');
        result.warnings?.forEach((warning) => toast.warning(warning));
        onClose();
      } catch (error) {
        // Mensagem real do backend (ex.: 400 "Invalid transfer payload" do
        // same-pipeline) — o interceptor global está suprimido via
        // `_handleErrorLocally` no service, então este é o ÚNICO toast.
        toast.error(getApiErrorMessage(error, 'Erro ao transferir ticket'));
      }
      return;
    }

    // Bulk (#158) — loop sequencial do endpoint single; tolera falha parcial
    // (um ticket inelegível/sem permissão não aborta o lote). Espelha o padrão
    // bulkResolve/bulkDelete da TicketsPage.
    let ok = 0;
    let failed = 0;
    const warnings: string[] = [];
    for (const id of targetIds) {
      try {
        const result = await transferMutation.mutateAsync({ ticketId: id, body });
        ok += 1;
        if (result.warnings?.length) warnings.push(...result.warnings);
      } catch {
        failed += 1;
      }
    }
    if (ok > 0) toast.success(`${ok} ticket${ok > 1 ? 's' : ''} transferido${ok > 1 ? 's' : ''}`);
    if (failed > 0) toast.error(`${failed} não transferido${failed > 1 ? 's' : ''}`);
    // Warnings deduplicados (round-robin de equipe pode repetir a mesma nota).
    [...new Set(warnings)].forEach((warning) => toast.warning(warning));
    onTransferred?.();
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      data-testid="ticket-transfer-dialog"
      className="fixed inset-0 z-[60] flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      /* form: NÃO fecha ao clicar fora (DS §Overlays dismissal) */
    >
      <div
        className="relative rounded-lg shadow-xl max-w-3xl w-full mx-4 overflow-hidden max-h-[85vh] flex flex-col"
        style={{ backgroundColor: '#ffffff', ...LIGHT_THEME_OVERRIDE }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: '#e5e7eb' }}>
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ backgroundColor: 'rgba(139, 92, 246, 0.1)', color: '#8b5cf6' }}
            >
              <Icon name="refresh" className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-lg font-semibold" style={{ color: '#1e293b' }}>
                {isBulk ? `Transferir ${targetIds.length} tickets` : 'Transferir ticket'}
              </h3>
              {isBulk ? (
                <p className="text-sm" style={{ color: '#64748b' }}>
                  Todos vão pro mesmo destino
                </p>
              ) : (ticketNumber || ticketTitle) ? (
                <p className="text-sm" style={{ color: '#64748b' }}>
                  {ticketNumber && <span className="font-mono">#{ticketNumber}</span>}
                  {ticketNumber && ticketTitle && ' — '}
                  {ticketTitle}
                </p>
              ) : null}
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="p-1 rounded transition-colors disabled:opacity-50"
            style={{ color: '#64748b' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f1f5f9';
              e.currentTarget.style.color = '#1e293b';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#64748b';
            }}
            data-testid="ticket-transfer-dialog-close"
          >
            <Icon name="x" className="w-5 h-5" />
          </button>
        </div>

        {/* Transfer Type Tabs */}
        <div className="border-b px-6" style={{ borderColor: '#e5e7eb' }}>
          <div className="flex gap-4">
            <button
              data-testid="ticket-transfer-dialog-tab-user"
              onClick={() => {
                setTransferType('user');
                setSelectedUserId(null);
              }}
              className="flex items-center gap-2 px-4 py-3 border-b-2 transition-colors"
              style={{
                borderColor: transferType === 'user' ? '#8b5cf6' : 'transparent',
                color: transferType === 'user' ? '#8b5cf6' : '#64748b',
              }}
            >
              <Icon name="user" className="w-4 h-4" />
              <span className="font-medium">Operador</span>
            </button>
            <button
              data-testid="ticket-transfer-dialog-tab-team"
              onClick={() => {
                setTransferType('team');
                setSelectedTeamId(null);
              }}
              className="flex items-center gap-2 px-4 py-3 border-b-2 transition-colors"
              style={{
                borderColor: transferType === 'team' ? '#8b5cf6' : 'transparent',
                color: transferType === 'team' ? '#8b5cf6' : '#64748b',
              }}
            >
              <Icon name="users" className="w-4 h-4" />
              <span className="font-medium">Equipe</span>
            </button>
            <button
              data-testid="ticket-transfer-dialog-tab-pipeline"
              onClick={() => {
                setTransferType('pipeline');
                setSelectedPipelineId(null);
                setSelectedStageId(null);
              }}
              className="flex items-center gap-2 px-4 py-3 border-b-2 transition-colors"
              style={{
                borderColor: transferType === 'pipeline' ? '#8b5cf6' : 'transparent',
                color: transferType === 'pipeline' ? '#8b5cf6' : '#64748b',
              }}
            >
              <Icon name="git-branch" className="w-4 h-4" />
              <span className="font-medium">Pipeline</span>
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="px-6 py-3 border-b" style={{ borderColor: '#e5e7eb' }}>
          <Input
            data-testid="ticket-transfer-dialog-search"
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={`Buscar ${
              transferType === 'user' ? 'operador' : transferType === 'team' ? 'equipe' : 'pipeline'
            }...`}
            icon="search"
            iconPosition="left"
          />
        </div>

        {/* Aviso estático — só na aba Pipeline (sem dado de elegibilidade no
            dropdown pra ser mais específico; texto genérico documentado). */}
        {transferType === 'pipeline' && (
          <div className="mx-6 mt-3 flex items-start gap-2 rounded-lg p-3 text-xs" style={{ backgroundColor: '#fef3c7', color: '#92400e' }}>
            <Icon name="alert-circle" className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>Se o responsável atual não for elegível no pipeline de destino, o ticket será desatribuído.</span>
          </div>
        )}

        {/* List */}
        <div className="flex-1 overflow-y-auto px-6 py-3">
          {transferType === 'user' && (
            <div className="space-y-2">
              {isLoadingUsers ? (
                <div className="text-center py-8">
                  <div className="flex items-center justify-center gap-2" style={{ color: '#64748b' }}>
                    <Icon name="loader" className="w-5 h-5 animate-spin" />
                    <p>Carregando operadores...</p>
                  </div>
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="text-center py-8">
                  <p style={{ color: '#64748b' }}>
                    {searchTerm ? 'Nenhum operador encontrado' : 'Nenhum operador disponível'}
                  </p>
                </div>
              ) : (
                filteredUsers.map((user) => (
                  <button
                    key={user.id}
                    data-testid={`ticket-transfer-dialog-user-option-${user.id}`}
                    onClick={() => setSelectedUserId(user.id)}
                    className="w-full text-left p-4 rounded-lg border-2 transition-all"
                    style={{
                      borderColor: selectedUserId === user.id ? '#8b5cf6' : '#e5e7eb',
                      backgroundColor: selectedUserId === user.id ? 'rgba(139, 92, 246, 0.1)' : 'transparent',
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0"
                        style={{
                          borderColor: selectedUserId === user.id ? '#8b5cf6' : '#e5e7eb',
                          backgroundColor: selectedUserId === user.id ? '#8b5cf6' : 'transparent',
                        }}
                      >
                        {selectedUserId === user.id && <Icon name="check" className="w-3 h-3 text-white" />}
                      </div>
                      <Avatar className="w-10 h-10" isSquircle>
                        <AvatarImage src={user.avatar} alt={user.name} isSquircle />
                        <AvatarFallback isSquircle>
                          <span className="text-indigo-600 font-semibold">
                            {user.name.charAt(0).toUpperCase()}
                          </span>
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium truncate" style={{ color: '#1e293b' }}>
                          {user.name}
                        </h4>
                        <p className="text-sm truncate" style={{ color: '#64748b' }}>
                          {user.email}
                        </p>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          )}

          {transferType === 'team' && (
            <div className="space-y-2">
              {filteredTeams.length === 0 ? (
                <div className="text-center py-8">
                  <p style={{ color: '#64748b' }}>
                    {searchTerm ? 'Nenhuma equipe encontrada' : 'Nenhuma equipe disponível'}
                  </p>
                </div>
              ) : (
                filteredTeams.map((team) => (
                  <button
                    key={team.id}
                    data-testid={`ticket-transfer-dialog-team-option-${team.id}`}
                    onClick={() => setSelectedTeamId(team.id)}
                    className="w-full text-left p-4 rounded-lg border-2 transition-all"
                    style={{
                      borderColor: selectedTeamId === team.id ? '#8b5cf6' : '#e5e7eb',
                      backgroundColor: selectedTeamId === team.id ? 'rgba(139, 92, 246, 0.1)' : 'transparent',
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0"
                        style={{
                          borderColor: selectedTeamId === team.id ? '#8b5cf6' : '#e5e7eb',
                          backgroundColor: selectedTeamId === team.id ? '#8b5cf6' : 'transparent',
                        }}
                      >
                        {selectedTeamId === team.id && <Icon name="check" className="w-3 h-3 text-white" />}
                      </div>
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: 'rgba(139, 92, 246, 0.1)', color: '#8b5cf6' }}
                      >
                        <Icon name="users" className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium" style={{ color: '#1e293b' }}>
                          {team.name}
                        </h4>
                        {team.description && (
                          <p className="text-sm" style={{ color: '#64748b' }}>
                            {team.description}
                          </p>
                        )}
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          )}

          {transferType === 'pipeline' && (
            <div className="space-y-3">
              <div className="space-y-2">
                {filteredPipelines.length === 0 ? (
                  <div className="text-center py-8">
                    <p style={{ color: '#64748b' }}>
                      {searchTerm ? 'Nenhum pipeline encontrado' : 'Nenhum pipeline disponível'}
                    </p>
                  </div>
                ) : (
                  filteredPipelines.map((pipeline) => (
                    <button
                      key={pipeline.id}
                      data-testid={`ticket-transfer-dialog-pipeline-option-${pipeline.id}`}
                      onClick={() => {
                        setSelectedPipelineId(pipeline.id);
                        setSelectedStageId(null);
                      }}
                      className="w-full text-left p-4 rounded-lg border-2 transition-all"
                      style={{
                        borderColor: selectedPipelineId === pipeline.id ? '#8b5cf6' : '#e5e7eb',
                        backgroundColor: selectedPipelineId === pipeline.id ? 'rgba(139, 92, 246, 0.1)' : 'transparent',
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0"
                          style={{
                            borderColor: selectedPipelineId === pipeline.id ? '#8b5cf6' : '#e5e7eb',
                            backgroundColor: selectedPipelineId === pipeline.id ? '#8b5cf6' : 'transparent',
                          }}
                        >
                          {selectedPipelineId === pipeline.id && <Icon name="check" className="w-3 h-3 text-white" />}
                        </div>
                        <span
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: pipeline.color }}
                        />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium truncate" style={{ color: '#1e293b' }}>
                            {pipeline.name}
                          </h4>
                          {pipeline.description && (
                            <p className="text-sm truncate" style={{ color: '#64748b' }}>
                              {pipeline.description}
                            </p>
                          )}
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>

              {selectedPipelineId && (
                <Select
                  label="Etapa (opcional)"
                  value={selectedStageId ?? ''}
                  onChange={(value) => setSelectedStageId(value || null)}
                  options={stageOptions}
                  placeholder="Manter etapa padrão do pipeline"
                  data-testid="ticket-transfer-dialog-stage-select"
                />
              )}
            </div>
          )}
        </div>

        {/* Notes */}
        <div className="px-6 py-3 border-t" style={{ borderColor: '#e5e7eb' }}>
          <label htmlFor="ticket-transfer-notes" className="block text-sm font-medium mb-2" style={{ color: '#1e293b' }}>
            Motivo / nota interna (opcional)
          </label>
          <Textarea
            id="ticket-transfer-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Contexto para quem recebe o ticket..."
            rows={3}
            resize="none"
            data-testid="ticket-transfer-dialog-notes"
          />
          <div
            className="flex items-start gap-2 rounded-lg p-3 text-xs mt-3"
            style={{ backgroundColor: '#eff6ff', color: '#1e40af' }}
          >
            <Icon name="info" className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>O histórico e o SLA do ticket são preservados; a transferência fica registrada na timeline.</span>
          </div>
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-end gap-3 px-6 py-4 border-t"
          style={{ backgroundColor: '#f1f5f9', borderColor: '#e5e7eb' }}
        >
          <button
            data-testid="ticket-transfer-dialog-cancel"
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium border rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ color: '#1e293b', backgroundColor: '#ffffff', borderColor: '#e5e7eb' }}
          >
            Cancelar
          </button>
          <button
            data-testid="ticket-transfer-dialog-confirm"
            onClick={() => { void handleTransfer(); }}
            disabled={isLoading || !canConfirm}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: '#8b5cf6' }}
          >
            {isLoading ? (
              <>
                <Icon name="loader" className="w-4 h-4 animate-spin" />
                <span>Transferindo...</span>
              </>
            ) : (
              <>
                <Icon name="refresh" className="w-4 h-4" />
                <span>Transferir</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TransferTicketDialog;
